// Improvememnts
// --> Write unit tests
// --> Memoize derived values more aggresibvely
// --> Add image tags or labels
// --> Support multiple selection sources eg: pre-filter by tags, collection etc
// TODO: --> Handle deleted images gracefully.
// --> Auto scroll to selected image when previewing or opening modal
// --> Display upload errors in modal
// TODO: --> Display a list of selected images separate from the other images.
// TODO: --> Prevent selection of corrupted images
// TODO: --> Implement sort options, filtering, pagination, clear all selections.

import { useState, useEffect, useRef, useCallback, useMemo } from "react";

// Import hooks and utils
import { useImageManager } from "@/hooks/useImageManager";
import CustomDropzoneCentralModal from "../dropzone/CustomDropzoneCentralModal";
import { ImageArraySchema } from "@/validationSchemas/imageSchema";
import { showUploadToast } from "@/utils/image-central-manager/toastHelpers";
import { useImageManagerUI } from "@/hooks/useImageManagerUI";
import { useImageSelections } from "@/stores/useImageSelections";

// Import reusable components
import ModalSearchInput from "../formInputFields/ModalSearchInput";
import ImagePreviewPanel from "./ImagePreviewPanel";
import ImageGrid from "./ImageGrid";

// Import inhouse icons
import { X, Sort, Image, Eye } from "../icons_new";

// Define a shared immutable empty array for use as a safe & stable fallback
// Will help in preventing unnecessary re-renders by preserving reference equality in helpers
const EMPTY_SELECTION = Object.freeze([]);

export default function ImageManagerModal() {
  // Pull the modal state, context config anf close handler from the global UI state
  const { isOpen, config, closeImageManager } = useImageManagerUI();

  // Extract configuration values with defaults and fallbacks
  const {
    contextKey = config?.contextKey ?? "default",
    maxSelect = 1,
    allowDuplicates = true,
    value = [],
    onSelect = () => {},
    onChange = () => {},
    setError = () => {},
    clearErrors = () => {},
  } = config;

  // Image selection actions from the global store
  const {
    getSelection,
    toggleImage,
    setSelection,
    clearSelection,
  } = useImageSelections();

  // Ref to access internal open() method of the dropzone child component
  const dropzoneRef = useRef(() => {});

  // Pull current list of uploaded images from central image manager store
  const { images } = useImageManager();

  // Derive initial selected image Ids by matching URLs from the 'value' prop with loaded images.
  const initialSelectedIds = useMemo(() => {
    if (!Array.isArray(value) || value.length === 0) return [];
    if (!Array.isArray(images) || images.length === 0) return [];

    const urlSet = new Set(value.map((v) => v?.url).filter(Boolean));
    return images
      .filter((img) => urlSet.has(img.url))
      .map((img) => img.instanceId);
  }, [images, value]);

  // Retrieve the list of selected image IDs for the current context key from the store
  const selectedImages = useImageSelections(
    useCallback((s) => s.selections[contextKey] ?? EMPTY_SELECTION, [contextKey])
  );
  // Track image being previewed
  const [selectedPreviewImage, setSelectedPreviewImage] = useState(null);

  // Toggle preview panel open/close when clicking the same Eye icon
  const handleTogglePreview = useCallback((img) => {
    setSelectedPreviewImage((prev) => (prev?.instanceId === img?.instanceId ? null : img));
  }, []);

  // Save state of the child dropzone component's internal open() function.
  const [modalDropzoneOpen, setModalDropzoneOpen] = useState(null);

  // Track selected image limit
  const [showLimitFeedback, setShowLimitFeedback] = useState(false);

  // Auto reset the feedback animation after 1 second
  useEffect(() => {
    if (showLimitFeedback) {
      const timeout = setTimeout(() => {
        setShowLimitFeedback(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [showLimitFeedback]);

  // Normalize maxSelect
  let normalizedMaxSelect;

  if (typeof maxSelect === "string" && maxSelect.toLowerCase().trim() === "unlimited") {
    normalizedMaxSelect = Infinity;
  } else if (typeof maxSelect === "number" && maxSelect > 0) {
    normalizedMaxSelect = maxSelect;
  } else {
    console.warn("Invalid maxSelect value. Defaulting to 1.");
    normalizedMaxSelect = 1;
  }

  const isUnlimited = normalizedMaxSelect === Infinity;

  // Validate allowDuplicate prop
  const safeAllowDuplicates = typeof allowDuplicates === "boolean" ? allowDuplicates : true;

  // Validate the value array using Zod
  const parsed = useMemo(() => ImageArraySchema.safeParse(value), [value]);

  // Extract the sanitized value but empty array if invalid
  const sanitizedValue = useMemo(() => parsed.success ? parsed.data : [], [parsed]);

  // Show warning + toast if validation failed
  useEffect(() => {
    if (!parsed.success) {
      console.warn("Some of the initial images are invalid:", parsed.error.format());
      showUploadToast({
        type: "warning",
        title: "Some selected images were invalid and removed.",
        subtitle: "Please check the image requirements and try again.",
      });
    }
  }, [parsed.success]);

  // save the state of the child dropzone 'open()' function.
  // Memoized to avoid re-renders
  const handleSetModalDropzoneOpen = useCallback((openFn) => {
    setModalDropzoneOpen(() => openFn);
  }, []);

  // On modal open, sync the initial selection into global store
  // Runs once per modal session and avoids resetting state unnecessarily
  useEffect(() => {
    if (!isOpen) return;

    if (images.length > 0) {
      const initialIds = initialSelectedIds;
      setSelection(contextKey, initialIds);
    } else if (value.length === 0) {
      // Ensure empty initial value clears any stale selection for this context
      try { clearSelection(contextKey); } catch {}
    }
  }, [isOpen, images.length, value.length, contextKey, setSelection, clearSelection, initialSelectedIds]);

  // Check if selection has changed compared to original 'value'
  // for disabling 'Confirm' button if nothing has changed
  const hasSelectionChanged = useMemo(() => {
    if (selectedImages.length !== initialSelectedIds.length) return true;
    return !selectedImages.every((id) => initialSelectedIds.includes(id));
  }, [selectedImages, initialSelectedIds]);

  // Calculate number of images added or removed to change the text button
  const selectionChanges = useMemo(() => {
    if (!hasSelectionChanged) return { added: 0, removed: 0 };

    const added = selectedImages.filter((id) => !initialSelectedIds.includes(id)).length;
    const removed = initialSelectedIds.filter((id) => !selectedImages.includes(id)).length;

    return { added, removed };
  }, [selectedImages, initialSelectedIds, hasSelectionChanged]);

  // Update the confirm button text
  const getButtonText = () => {
    if (!hasSelectionChanged) return "Add";

    const { added, removed } = selectionChanges;

    if (added > 0 && removed > 0) {
      return `Update (${added} added, ${removed} removed)`;
    } else if (added > 0) {
      return `Add ${added}`;
    } else if (removed > 0) {
      return `Remove ${removed}`;
    }

    return "Update";
  };

  const toggleImageSelection = (instanceId) => {
    if (!isOpen) return;

    const image = images.find((img) => img.instanceId === instanceId);
    if (!image) return;

    // Current selection snapshot
    const currentSelected = getSelection(contextKey) ?? [];

    // If clicking an already-selected image, simply deselect it.
    if (currentSelected.includes(instanceId)) {
      toggleImage(contextKey, instanceId, normalizedMaxSelect);

      if (typeof onChange === "function") {
        const selected = images.filter((img) => getSelection(contextKey).includes(img.instanceId));
        onChange(selected);
      }
      return;
    }

    // Single-select mode should replace the previous selection with the new one.
    if (!isUnlimited && normalizedMaxSelect === 1) {
      setSelection(contextKey, [instanceId]);

      if (typeof onChange === "function") {
        const selected = images.filter((img) => getSelection(contextKey).includes(img.instanceId));
        onChange(selected);
      }
      return;
    }

    // Check duplicate logic
    // Build list of selected image objects from selected instanceIds
    const selectedImageObjects = images.filter((img) =>
      (getSelection(contextKey) ?? []).includes(img.instanceId)
    );

    // Check if an image with the same URL already exists in selection
    const isDuplicate = selectedImageObjects.some((img) => {
      if (img?.id && image?.id) return img.id === image.id;
      return img.url === image.url;
    });

    // If allowDuplicates flag is false and it's a duplicate, show toast and block
    if (!safeAllowDuplicates && isDuplicate) {
      showUploadToast({
        type: "warning",
        title: "Duplicate media not allowed",
        subtitle: "This image is already selected.",
      });
      return;
    }

    // If over limit, show feedback
    if (
      !isUnlimited && 
      (getSelection(contextKey)?.length ?? 0) >= normalizedMaxSelect && 
      !(getSelection(contextKey) ?? []).includes(instanceId)
    ) {
      setShowLimitFeedback(true);
      return;
    }

    toggleImage(contextKey, instanceId, normalizedMaxSelect);

    if (typeof onChange === "function") {
      const selected = images.filter((img) => getSelection(contextKey).includes(img.instanceId));
      onChange(selected);
    }
  };


  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#000]/45 backdrop-blur-sm"
      onClick={closeImageManager}
    >
      {/* Actual modal box */}
      <div
        className="w-[1100px] h-[80%] flex flex-col bg-white rounded-lg overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex text-[#000000] items-center justify-between bg-[#ececec] py-4 px-6 rounded-t-lg">
          <span className="font-medium">Select file</span>
          <button
            type="button"
            onClick={closeImageManager}
            className="rounded-md p-1 transition hover:bg-[#f3f4f6]"
            aria-label="Close image manager"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Main content + Preview */}
        <div className="flex flex-1 overflow-hidden">
          {/* Main image manager content */}
          <div
            className={`transition-all duration-300 ${
              selectedPreviewImage ? "w-[calc(100%-320px)]" : "w-full"
            }`}
          >
            <div className="px-6 h-full">
              {/* Check if modal is empty to show empty state */}
              {images.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center py-10 text-center text-[#777]">
                  <div className="hidden">
                    <CustomDropzoneCentralModal
                      value={sanitizedValue}
                      onChange={onChange}
                      setError={setError}
                      clearErrors={clearErrors}
                      dropzoneRef={dropzoneRef}
                      onOpenChange={handleSetModalDropzoneOpen}
                    />
                  </div>

                  <div className="w-full flex flex-col items-center justify-center py-16 px-4 text-center">
                    <Image className="w-12 h-12 text-[#b5b5b5] mb-4" />
                    <h2 className="text-lg font-semibold text-[#000] dark:text-white mb-2">
                      No images yet
                    </h2>
                    <p className="text-sm text-[#808080] dark:text-gray-300 mb-6 max-w-md">
                      Upload images to make a selection. You’ll also be able to reuse them later.
                    </p>
                    <button
                      onClick={() => {
                        modalDropzoneOpen?.();
                      }}
                      className="bg-[#000] hover:bg-[#393939] text-white font-semibold px-4 py-2 rounded-md transition duration-200"
                    >
                      Upload new image
                    </button>
                  </div>
                </div>
              ) : (
                // Content area if not empty
                <div className="h-full">
                  {/* Search and sort */}
                  <div className="px-6 py-6 flex justify-between">
                    <ModalSearchInput />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="border border-[#a0a0a0] h-fit flex items-center justify-center gap-[10px] rounded-[10px] px-[10px] py-[5px] text-[15px] font-medium shadow-md hover:bg-[#fbfbfb] transition"
                      >
                        <Sort className="h-[19px] w-[19px] text-[#000]" />
                        Sort
                      </button>
                    </div>
                  </div>

                  {/* Dropzone */}
                  <div className="px-6 mb-6">
                    <CustomDropzoneCentralModal
                      value={sanitizedValue}
                      onChange={onChange}
                      setError={setError}
                      clearErrors={clearErrors}
                      dropzoneRef={dropzoneRef}
                      onOpenChange={handleSetModalDropzoneOpen}
                    />
                  </div>

                  {/* Image list */}
                  <ImageGrid
                    images={images}
                    selectedImages={selectedImages}
                    toggleImageSelection={toggleImageSelection}
                    setSelectedPreviewImage={handleTogglePreview}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Preview Panel */}
          {selectedPreviewImage && (
            <ImagePreviewPanel
              image={selectedPreviewImage}
              onClose={() => setSelectedPreviewImage(null)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 py-3 px-5 border-t border-[#d2d2d2]">
          {/* Selected count text */}
          <div
            className={`mr-auto text-sm font-medium transition-all duration-300 ${
              showLimitFeedback ? "text-red-500" : "text-[#000]"
            }`}
            style={
              showLimitFeedback
                ? {
                    animation: "shake 0.4s ease-in-out",
                  }
                : {}
            }
          >
            {isUnlimited
              ? `${selectedImages.length} images selected`
              : `${selectedImages.length}/${normalizedMaxSelect} images selected`}
          </div>

          <button
            onClick={() => {
              try { clearSelection(contextKey); } catch {}
              closeImageManager();
            }}
            className="px-4 py-2 text-sm font-medium text-[#000] bg-white border border-[#9e9e9e] rounded-[10px] hover:bg-[#f3f3f3]"
          >
            Cancel
          </button>
          
          <button
            onClick={() => {
              const selected = images.filter((img) => selectedImages.includes(img.instanceId));
              onSelect(selected);

              try { clearSelection(contextKey); } catch {}
              closeImageManager();
            }}
            disabled={!hasSelectionChanged}
            className={`px-6 py-2 text-sm font-medium text-white rounded-[10px] transition
              bg-[#000] hover:bg-[#414141]
              disabled:bg-[#c9c9c9] disabled:text-[#555] disabled:cursor-not-allowed disabled:hover:bg-[#c9c9c9]
            `}
          >
            {getButtonText()}
          </button>
        </div>

        {/* Animation keyframes of the selected count text */}
        <style>
          {`
            @keyframes shake {
              0%, 100% { transform: translateX(0); }
              25% { transform: translateX(-4px); }
              50% { transform: translateX(4px); }
              75% { transform: translateX(-2px); }
            }
          `}
        </style>
      </div>
    </div>
  );
}
